import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { StatusBadge, InitialsBadge } from "../../components/DashboardLayout";
import { ChevronRight, Plus, Search, X } from "lucide-react";
import { toast, Toaster } from "sonner";

const STATUSES = [
    { id: "all", label: "Tous" },
    { id: "en_cours", label: "En cours" },
    { id: "depart", label: "Départ" },
    { id: "finalise", label: "Finalisés" },
];

export default function Clients() {
    const [items, setItems] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [showNew, setShowNew] = useState(false);

    const load = async () => {
        try {
            const [c, m] = await Promise.all([
                api.get("/clients", { params: filter !== "all" ? { statut: filter } : {} }),
                api.get("/dashboard/metrics"),
            ]);
            setItems(c.data);
            setMetrics(m.data);
        } catch (e) {
            toast.error("Erreur de chargement");
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [filter]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((c) => `${c.prenom} ${c.nom} ${c.niveau}`.toLowerCase().includes(q));
    }, [items, search]);

    return (
        <div data-testid="dashboard-clients" className="space-y-8">
            <Toaster position="top-center" theme="dark" richColors />
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <span className="text-xs tracking-[0.3em] text-ice uppercase">— Tableau de bord</span>
                    <h1 className="font-serif text-4xl mt-2">Clients</h1>
                </div>
                <button
                    data-testid="btn-new-client"
                    onClick={() => setShowNew(true)}
                    className="inline-flex items-center gap-2 bg-ice text-ink-900 hover:bg-white transition-colors px-5 py-2.5 rounded-md text-sm font-medium"
                >
                    <Plus size={16} /> Nouveau client
                </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Metric label="Clients actifs" value={metrics?.clients_actifs ?? "—"} />
                <Metric label="En cours" value={metrics?.en_cours ?? "—"} />
                <Metric label="Rendez-vous cette semaine" value={metrics?.rdv_semaine ?? "—"} />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                        <button
                            key={s.id}
                            data-testid={`filter-${s.id}`}
                            onClick={() => setFilter(s.id)}
                            className={`px-4 py-2 rounded-md text-sm border transition-colors ${
                                filter === s.id
                                    ? "bg-ice text-ink-900 border-ice"
                                    : "border-white/15 text-white/75 hover:border-ice/40 hover:text-ice"
                            }`}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
                <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher un client…"
                        data-testid="clients-search"
                        className="bg-ink-600 border border-white/10 text-white placeholder-white/40 focus:border-ice focus:ring-1 focus:ring-ice rounded-md pl-9 pr-4 py-2 text-sm outline-none"
                    />
                </div>
            </div>

            <div className="bg-ink-600 rounded-xl border border-white/10 overflow-hidden">
                {filtered.length === 0 ? (
                    <div className="p-10 text-center text-white/60 text-sm">Aucun client.</div>
                ) : (
                    filtered.map((c) => (
                        <Link
                            key={c.id}
                            to={`/dashboard/clients/${c.id}`}
                            data-testid={`client-row-${c.id}`}
                            className="flex items-center gap-4 p-4 md:p-5 border-b border-white/5 last:border-0 hover:bg-ink-700 transition-colors"
                        >
                            <InitialsBadge value={c.initials} />
                            <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">
                                    {c.prenom} {c.nom}
                                </div>
                                <div className="text-xs text-white/50 mt-0.5">{c.niveau}</div>
                            </div>
                            <div className="hidden sm:block">
                                <StatusBadge status={c.statut} />
                            </div>
                            <div className="hidden md:block">
                                <InitialsBadge value={c.suivi_par} size="sm" tone="ice" />
                            </div>
                            <ChevronRight size={18} className="text-white/40" />
                        </Link>
                    ))
                )}
            </div>

            {showNew && <NewClientModal onClose={() => setShowNew(false)} onCreated={load} />}
        </div>
    );
}

function Metric({ label, value }) {
    return (
        <div className="bg-ink-600 border border-white/10 rounded-xl p-6">
            <div className="text-xs tracking-[0.2em] text-white/55 uppercase">{label}</div>
            <div className="font-serif text-4xl mt-2">{value}</div>
        </div>
    );
}

function NewClientModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        prenom: "",
        nom: "",
        email: "",
        telephone: "",
        parent_email: "",
        niveau: "Terminale - Parcoursup",
        statut: "en_cours",
        suivi_par: "CC",
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/clients", form);
            toast.success("Client créé");
            onCreated();
            onClose();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-ink-700 rounded-xl border border-ice/20 max-w-xl w-full p-7 max-h-[90vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between mb-5">
                    <h3 className="font-serif text-2xl">Nouveau client</h3>
                    <button onClick={onClose} className="text-white/50 hover:text-white">
                        <X size={20} />
                    </button>
                </div>
                <form onSubmit={submit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <Input label="Prénom" value={form.prenom} onChange={(v) => set("prenom", v)} required />
                        <Input label="Nom" value={form.nom} onChange={(v) => set("nom", v)} required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Input label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} required />
                        <Input label="Téléphone" value={form.telephone} onChange={(v) => set("telephone", v)} />
                    </div>
                    <Input label="Email parent" type="email" value={form.parent_email} onChange={(v) => set("parent_email", v)} />
                    <Input label="Niveau / Stage" value={form.niveau} onChange={(v) => set("niveau", v)} required />
                    <div className="grid grid-cols-2 gap-4">
                        <Select label="Statut" value={form.statut} onChange={(v) => set("statut", v)}
                                options={[["en_cours", "En cours"], ["depart", "Départ"], ["finalise", "Finalisé"]]} />
                        <Select label="Suivi par" value={form.suivi_par} onChange={(v) => set("suivi_par", v)}
                                options={[["CC", "CC"], ["CCJ", "CCJ"]]} />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="modal-submit-client"
                        className="w-full bg-ice text-ink-900 hover:bg-white disabled:opacity-50 transition-colors px-6 py-3 rounded-md font-medium"
                    >
                        {submitting ? "Création…" : "Créer le client"}
                    </button>
                </form>
            </div>
        </div>
    );
}

function Input({ label, value, onChange, type = "text", required }) {
    return (
        <label className="block">
            <span className="text-xs tracking-[0.2em] text-white/60 uppercase">{label}</span>
            <input
                type={type}
                value={value}
                required={required}
                onChange={(e) => onChange(e.target.value)}
                className="w-full mt-1.5 bg-ink-800 border border-white/15 text-white focus:border-ice focus:ring-1 focus:ring-ice rounded-md px-4 py-2.5 outline-none text-sm"
            />
        </label>
    );
}

function Select({ label, value, onChange, options }) {
    return (
        <label className="block">
            <span className="text-xs tracking-[0.2em] text-white/60 uppercase">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full mt-1.5 bg-ink-800 border border-white/15 text-white focus:border-ice focus:ring-1 focus:ring-ice rounded-md px-4 py-2.5 outline-none text-sm"
            >
                {options.map(([v, l]) => (
                    <option key={v} value={v}>
                        {l}
                    </option>
                ))}
            </select>
        </label>
    );
}
